/**
 * Currency utility functions for dynamic currency formatting
 */

// Currency symbols mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  JPY: '¥',
  CNY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RUB: '₽',
  TRY: '₺',
  BRL: 'R$',
  MXN: '$',
  DOP: 'RD$',
  ZAR: 'R',
  GHS: 'GH₵',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  KRW: '₩',
  THB: '฿',
  MYR: 'RM',
  IDR: 'Rp',
  PHP: '₱',
  VND: '₫',
  AED: 'د.إ',
  SAR: '﷼',
  QAR: 'ر.ق',
  KWD: 'د.ك',
  BHD: '.د.ب',
  OMR: 'ر.ع.',
  ILS: '₪',
  EGP: '£',
  JOD: 'د.ا',
  LBP: 'ل.ل',
  PKR: '₨',
  LKR: '₨',
  BDT: '৳',
  NPR: '₨',
  AFN: '؋',
  IRR: '﷼',
  IQD: 'ع.د',
  KZT: '₸',
  UZS: 'лв',
  AMD: '֏',
  GEL: '₾',
  AZN: '₼',
  TMT: 'm',
  KGS: 'лв',
  TJS: 'ЅМ',
  MNT: '₮',
  UAH: '₴',
  BYN: 'Br',
  MDL: 'L',
  RSD: 'дин.',
  MKD: 'ден',
  ALL: 'L',
  BAM: 'KM',
  // Add more currencies as needed
};

// Decimal places for different currencies
const CURRENCY_DECIMALS: Record<string, number> = {
  JPY: 0,  // Japanese Yen has no decimal places
  KRW: 0,  // Korean Won has no decimal places
  VND: 0,  // Vietnamese Dong has no decimal places
  IDR: 0,  // Indonesian Rupiah has no decimal places
  CLP: 0,  // Chilean Peso has no decimal places
  TWD: 0,  // Taiwan Dollar has no decimal places
  // Most other currencies use 2 decimal places (default)
};

/**
 * Get currency symbol for a given currency code
 */
export function getCurrencySymbol(currencyCode: string | undefined): string {
  if (!currencyCode) {
    return getCurrencySymbol(getDefaultCurrency());
  }
  const upperCode = currencyCode.toUpperCase();
  return CURRENCY_SYMBOLS[upperCode] || upperCode;
}

/**
 * Get decimal places for a given currency
 */
export function getCurrencyDecimals(currencyCode: string | undefined): number {
  if (!currencyCode) {
    return getCurrencyDecimals(getDefaultCurrency());
  }
  const upperCode = currencyCode.toUpperCase();
  return CURRENCY_DECIMALS[upperCode] ?? 2; // Default to 2 decimal places
}

/**
 * Format number with thousands separators and proper decimal places
 */
export function formatNumberWithSeparators(amount: number, decimals: number = 2): string {
  // A missing or malformed amount must degrade to 0, never crash the
  // screen — this renders in guest-facing money surfaces.
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    amount = 0;
  }
  try {
    // Use toLocaleString for proper thousands separators
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  } catch (error) {
    // Fallback for older browsers
    const fixed = amount.toFixed(decimals);
    return fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

/**
 * Format amount with currency symbol and proper decimal places
 * Includes thousands separators and proper localization
 */
export function formatCurrency(amount: number, currencyCode: string | undefined): string {
  const currency = currencyCode || getDefaultCurrency();
  const symbol = getCurrencySymbol(currency);
  const decimals = getCurrencyDecimals(currency);
  
  // Format number with thousands separators
  const formattedNumber = formatNumberWithSeparators(amount, decimals);
  
  return `${symbol}${formattedNumber}`;
}

/**
 * Format amount with currency code (e.g., "1,110.70 INR")
 */
export function formatCurrencyWithCode(amount: number, currencyCode: string | undefined): string {
  const currency = currencyCode || getDefaultCurrency();
  const decimals = getCurrencyDecimals(currency);
  const formattedNumber = formatNumberWithSeparators(amount, decimals);
  return `${formattedNumber} ${currency.toUpperCase()}`;
}

/**
 * Format amount with both symbol and code (e.g., "₹1,110.70 INR")
 */
export function formatCurrencyFull(amount: number, currencyCode: string | undefined): string {
  const currency = currencyCode || getDefaultCurrency();
  const symbol = getCurrencySymbol(currency);
  const decimals = getCurrencyDecimals(currency);
  const upperCode = currency.toUpperCase();
  const formattedNumber = formatNumberWithSeparators(amount, decimals);
  
  return `${symbol}${formattedNumber} ${upperCode}`;
}

/**
 * Format currency for payment gateway display
 */
export function formatPaymentAmount(amount: number, currencyCode: string | undefined): string {
  // For payment gateways, we usually want symbol + amount
  return formatCurrency(amount, currencyCode);
}

/**
 * Get currency info object
 */
export function getCurrencyInfo(currencyCode: string | undefined) {
  const currency = currencyCode || getDefaultCurrency();
  const upperCode = currency.toUpperCase();
  return {
    code: upperCode,
    symbol: getCurrencySymbol(currency),
    decimals: getCurrencyDecimals(currency),
  };
}

/**
 * Check if currency code is valid
 */
export function isValidCurrencyCode(currencyCode: string): boolean {
  const upperCode = currencyCode.toUpperCase();
  return upperCode.length === 3 && /^[A-Z]{3}$/.test(upperCode);
}

/**
 * Get default currency (fallback)
 */
export function getDefaultCurrency(): string {
  return 'INR'; // Default to INR as per project requirements
}

/**
 * Format compact currency with proper thousands separators (e.g., "₹1.2K", "₹1,234")
 */
export function formatCurrencyCompact(amount: number, currencyCode: string | undefined): string {
  const currency = currencyCode || getDefaultCurrency();
  const symbol = getCurrencySymbol(currency);
  
  if (amount >= 1_000_000) {
    return `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  } else if (amount >= 1_000) {
    return `${symbol}${(amount / 1_000).toFixed(1)}K`;
  } else {
    return formatCurrency(amount, currency);
  }
}

/**
 * Format currency for input fields (without symbol, with separators)
 */
export function formatCurrencyInput(amount: number, currencyCode: string | undefined): string {
  const currency = currencyCode || getDefaultCurrency();
  const decimals = getCurrencyDecimals(currency);
  return formatNumberWithSeparators(amount, decimals);
}

/**
 * Parse formatted currency string to number
 */
export function parseCurrencyString(value: string): number {
  // Remove all non-numeric characters except decimal point
  const numericString = value.replace(/[^\d.-]/g, '');
  const parsed = parseFloat(numericString);
  return isNaN(parsed) ? 0 : parsed;
}